import { SplitFactory } from '@splitsoftware/splitio';
import { UnicornApi, AbTestKey, EnvironmentName, Account, ProductEnvironment, User } from '@cld/unicorn-api';
import { ConsoleLogger, StorageClient, StorageResult } from '../state/types';

const splitioAuthorizationKeys: Record<EnvironmentName, string> = {
  localhost: 'd0909j5j0rnbqbv0g515dui7h7euq52lmm7b',
  development: 'd0909j5j0rnbqbv0g515dui7h7euq52lmm7b',
  nightly: 'd0909j5j0rnbqbv0g515dui7h7euq52lmm7b',
  staging: 'd0909j5j0rnbqbv0g515dui7h7euq52lmm7b',
  production: 'mppsihgtif1foc21phmnag1cljr74jl9kebj',
  eod: 'd0909j5j0rnbqbv0g515dui7h7euq52lmm7b',
};

const isOneOf = <ItemType, TargetType extends ItemType>(array: readonly TargetType[], item: ItemType): item is TargetType =>
  array.includes(item as TargetType);

export const trimGaCookie = (gaCookie?: string) => gaCookie?.replace(/GA\d+\.\d+\./, '');
export const trimProtocol = (url: string) => url.replace(`${new URL(url).protocol}//`, '');
export const getCookieValue = (cookies: string, cookieName: string) =>
  cookies
    .split('; ')
    .find((entry) => entry.startsWith(`${cookieName}=`))
    ?.split('=')?.[1];

// Any changes MUST be approved by data team
const createAnalyticsEvent = ({
  cloudName,
  currentUrl,
  data,
  environment,
  productEnvironmentExternalId,
  cookies,
}: {
  data: SplitIO.ImpressionData;
  cloudName: string;
  productEnvironmentExternalId: string;
  environment: string;
  currentUrl: string;
  cookies: string;
}) => ({
  environment,
  split_key: data.impression.keyName,
  split_name: data.impression.feature,
  split_treatment: data.impression.treatment,
  split_label: data.impression.label,
  split_version: data.impression.changeNumber,
  cloud_name: cloudName,
  customer_external_id: productEnvironmentExternalId,
  ga_cookie_id: trimGaCookie(getCookieValue(cookies, '_ga')),
  url: trimProtocol(currentUrl),
});

const createCacheKey = (data: SplitIO.ImpressionData) => `${data.impression.keyName}-${data.impression.feature}-${data.impression.treatment}`;

export type AbTestingClient = UnicornApi['methods']['getAbTestTreatment'];

export type AbTestingClientFactory = (configuration: {
  account: Account;
  productEnvironment: ProductEnvironment;
  user: Omit<User, 'impersonationActive'>;
}) => AbTestingClient;

export const createAlwaysFailingAbTestingClientFactory =
  ({ consoleLogger }: { consoleLogger: ConsoleLogger }): AbTestingClientFactory =>
  () =>
  ({ testId }) => {
    consoleLogger.error('abTesting', 'Force failed treatment resolution (unicorn devtools setting)', { testId });
    return Promise.resolve({ status: 'error' });
  };

export const createAbTestingClientFactory =
  ({
    baseFetch,
    environmentName,
    persistentStorage,
    consoleLogger,
  }: {
    baseFetch: typeof window.fetch;
    environmentName: EnvironmentName;
    persistentStorage: StorageClient;
    consoleLogger: ConsoleLogger;
  }): AbTestingClientFactory =>
  ({ account, productEnvironment, user }) => {
    const splitioKeyMap: Record<AbTestKey, string> = { account: account.externalId };
    const alwaysSentAttributes = { account_created_at: new Date(account.__MIGRATION_createdAt), user_role: user.role };

    const impressionEventCache = persistentStorage.createController<string[]>('ab-testing-impression-event-cache');
    const initialCache = impressionEventCache.get();
    if (initialCache.status === 'error') {
      impressionEventCache.set([]);
      consoleLogger.debug('abTesting', 'Empty impression event cache created');
    }

    const checkCache = (cacheResult: StorageResult<string[]>) => {
      if (cacheResult.status === 'error') {
        consoleLogger.warning('abTesting', 'Impression cache corrupted - resetting');
        impressionEventCache.set([]);
      }

      return impressionEventCache.get();
    };

    const logImpression = (data: SplitIO.ImpressionData) => {
      const eventData = createAnalyticsEvent({
        cloudName: productEnvironment.cloudName,
        currentUrl: window.location.href,
        cookies: document.cookie,
        data,
        environment: environmentName,
        productEnvironmentExternalId: productEnvironment.externalId,
      });

      const cache = impressionEventCache.get();
      const cacheKey = createCacheKey(data);

      const isCached = cache.status === 'success' && cache.value.includes(cacheKey);

      checkCache(cache);

      if (isCached) {
        consoleLogger.debug('abTesting', 'Impression event cache hit', { splitEvent: data, eventData });
        return;
      }

      const flattenedEvent = Object.entries(eventData)
        .filter((keyValue): keyValue is [(typeof keyValue)[0], Exclude<(typeof keyValue)[1], undefined>] => keyValue[1] !== undefined)
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join('&');

      void baseFetch(`https://analytics-api.cloudinary.com/splitio_impression?${flattenedEvent}`)
        .then(() => {
          const cacheResult = checkCache(cache);

          if (cacheResult.status === 'success') {
            consoleLogger.debug('abTesting', 'Sent analytics impression event', eventData);
            impressionEventCache.set(cacheResult.value.concat(cacheKey));
            consoleLogger.debug('abTesting', 'Cached impression event', { cacheKey, eventData });
          }
        })
        .catch(() => {
          const cacheResult = checkCache(cache);

          if (cacheResult.status === 'success') {
            consoleLogger.error('abTesting', 'Failed sending impression event', eventData);
            impressionEventCache.set(cacheResult.value.filter((value) => value === cacheKey));
            consoleLogger.warning('abTesting', 'Invalidated cache value', { cacheKey, eventData });
          }
        });
    };

    const accountBasedClient = SplitFactory({
      core: {
        authorizationKey: splitioAuthorizationKeys[environmentName],
        key: splitioKeyMap.account,
        trafficType: 'user',
      },
      startup: { readyTimeout: 3 },
      impressionListener: {
        logImpression,
      },
    }).client();

    consoleLogger.debug('abTesting', 'AB Testing client configured (Split.io)');

    return async ({ assignedTo, testId, attributes, supportedTreatments }) => {
      const readyResult = await accountBasedClient
        .ready()
        .then(() => 'success')
        .catch(() => 'error');

      if (readyResult === 'error') {
        consoleLogger.error('abTesting', 'Split client failed to initialize', { testId });
        return { status: 'error' };
      }

      const convertAttributes = (rawAttributes: Exclude<typeof attributes, undefined>): SplitIO.Attributes =>
        Object.fromEntries(
          Object.entries(rawAttributes).map(([key, value]) => {
            if (value instanceof Date) {
              return [key, value.getTime()];
            }
            return [key, value];
          })
        );

      const preparedAttributes = convertAttributes(Object.assign({}, attributes, alwaysSentAttributes));
      const treatment = accountBasedClient.getTreatment(testId, preparedAttributes);

      if (treatment === 'control') {
        consoleLogger.error('abTesting', 'Control treatment received - split integration error', { testId });
        return { status: 'error' };
      }

      if (!isOneOf(supportedTreatments, treatment)) {
        consoleLogger.error('abTesting', 'Unsupported A/B test treatment received', {
          testId,
          expectedOneOf: supportedTreatments,
          received: treatment,
        });
        return { status: 'error' };
      }

      consoleLogger.debug('abTesting', 'Resolved A/B test treatment', {
        assignedTo,
        assignedKey: splitioKeyMap[assignedTo],
        attributes: preparedAttributes,
        testId,
        treatment,
      });

      return { status: 'success', treatment };
    };
  };
